import { useMatomo } from '@datapunt/matomo-tracker-react';
import { valibotResolver } from '@hookform/resolvers/valibot';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useBlocker } from 'react-router-dom';

import EmailNotificationsProfile from '@components/common/EmailNotificationsProfile/EmailNotificationsProfile';
import {
  selectHasSubscriptionsChanges,
  selectSubscriptions,
  useSubscriptionsActions,
} from '@components/common/EmailNotificationsProfile/store/useSubscriptionsProfileStore';
import Header from '@components/common/Header/Header';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useActionItemsStorePersistAction } from '@pages/ActionItems/store/useActionItemsStorePersist';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useChartsReadingTypesQuery } from '@services/useChartsReadingTypesQuery';
import { useLanguageListQuery } from '@services/useLanguageListQuery';
import { useTimezoneQuery } from '@services/useTimezoneQuery';
import { useUserEmailEventDictionaryQuery } from '@services/user/useUserEmailEventDictionaryQuery';
import { useUserFullProfileQuery } from '@services/user/useUserFullProfileQuery';
import { useUserUpdateMutation } from '@services/user/useUserUpdateMutation';

import { selectAppPersistRefreshInterval, useAppPersistActions } from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import AdditionalInfo from './components/AdditionalInfo/AdditionalInfo';
import DataUnitsInfo from './components/DataUnitsInfo/DataUnitsInfo';
import MainInfo from './components/MainInfo/MainInfo';
import PersonalInfo from './components/PersonalInfo/PersonalInfo';
import SecurityInfo from './components/SecurityInfo/SecurityInfo';
import ThemeBlock from './components/ThemeBlock/ThemeBlock';
import { PROFILE_FIELDS, profileDefaultValues, schemaProfile } from './constants/schemaProfile';
import { addDisabledField } from './helpers/addDisabledField';
import { getReloadTimeoutList } from './helpers/getReloadTimeoutList';
import style from './style.module.scss';

const Profile = () => {
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();

  const { setAppPersistRefreshInterval } = useAppPersistActions();
  const { setAIResetFilterState } = useActionItemsStorePersistAction();
  const { setActiveModal } = useModalActions();
  const { setDefaultSubscriptions } = useSubscriptionsActions();

  const { data: fullProfileData, isFetching: profileIsFetching } = useUserFullProfileQuery();
  const { data: emailEventsData } = useUserEmailEventDictionaryQuery();
  const { data: chartsReadingTypesData } = useChartsReadingTypesQuery();
  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: timezoneListData, isLoading: isTimezoneLoading } = useTimezoneQuery();
  const { data: languageListData } = useLanguageListQuery();

  const { mutate: userUpdateMutate, isLoading: updateMutationLoading } = useUserUpdateMutation({
    userId: fullProfileData?.user?.id,
  });

  const refreshInterval = selectAppPersistRefreshInterval() / 1000;

  const subscriptionsValue = selectSubscriptions();
  const hasSubscriptionsChanges = selectHasSubscriptionsChanges();

  const isLoading = profileIsFetching || updateMutationLoading;
  const identityProvider = fullProfileData?.customer.identity_provider;

  const setNotification = () => {
    const userSubscriptions = Object.values(fullProfileData?.user?.email_subscriptions || {}).filter(
      (ev) => ev.event?.with_settings,
    );

    setDefaultSubscriptions({
      emailEventsDictionary: emailEventsData?.emailEvents,
      userSubscriptions: !!userSubscriptions.length && userSubscriptions,
      defaultLocs: [],
    });
  };

  useEffect(() => {
    if (fullProfileData?.user) {
      setNotification();
    }
  }, [fullProfileData?.user]);

  const reloadTimeoutList = getReloadTimeoutList(t);

  const defaultFormValues = profileDefaultValues(
    t,
    reloadTimeoutList,
    refreshInterval,
    fullProfileData?.user,
    fullProfileData?.simpleEventsDictionary,
    languageListData,
    timezoneListData,
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty },
    setError,
  } = useForm({
    mode: 'all',
    values: defaultFormValues,
    resolver: valibotResolver(schemaProfile),
  });

  const shouldBlock = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string };
      nextLocation: { pathname: string };
    }) => {
      return isDirty && currentLocation.pathname !== nextLocation.pathname;
    },
    [isDirty],
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setActiveModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          bodyTitle: t('Unsaved changes'),
          bodyContent: t(
            'Your recent edits have not been saved. Please ensure all modifications are saved before leaving this page to avoid data loss.',
          ),
          cancelText: t('Discard Changes'),
          onClose: () => blocker.reset(),
          onCancel: () => blocker.proceed(),
          confirmText: t('Go Back'),
          onConfirm: () => blocker.reset(),
        },
      });
    }
  }, [blocker]);

  useEffect(() => {
    trackPageView({});
  }, []);

  const submit = handleSubmit((formData) => {
    setAppPersistRefreshInterval(+formData.refresh_interval.value);

    const aiUrgencies = [];

    for (const key in formData.action_list_urgencies) {
      if (formData.action_list_urgencies[key]) {
        aiUrgencies.push(key);
      }
    }

    const units: { [key: number]: 'metric' | 'us' } = {};

    if (chartsReadingTypesData?.length) {
      chartsReadingTypesData.forEach(
        (readingType) => (units[readingType.id] = +formData.use_metric === 0 ? 'metric' : 'us'),
      );
    }

    userUpdateMutate(
      {
        ...formData,
        id: fullProfileData?.user.id,
        email_events: JSON.stringify(subscriptionsValue),
        language_iso_code: formData.language_iso_code.iso_code,
        timezone_id: formData.timezone_id.id,
        action_list_urgencies: aiUrgencies,
        personal_settings: {
          units,
          datetime_format: formData.datetime_format,
        },
        userpic: '',
        receive_own_emails: +formData.receive_own_emails,
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('Your changes have been saved!') });
          reset(defaultFormValues);
          setAIResetFilterState();
        },
        onError: (error: any) => {
          const errorData = error?.response?.data?.errors;
          if (errorData?.email) {
            setError(PROFILE_FIELDS.EMAIL, { message: errorData?.email[0] });
          }
          if (errorData?.username) {
            setError(PROFILE_FIELDS.USERNAME, { message: errorData?.username[0] });
          }
          setNotification();
        },
      },
    );
  });

  const handleResetChanges = () => {
    reset();
    setNotification();
  };

  const saveData = async () => {
    const isValid = await trigger();

    if (isValid) {
      submit();
    }
  };

  const { processedFacilityStructureData, activeLocationsIds } = useMemo(
    () => addDisabledField(assetTreeData?.facilities),
    [assetTreeData?.facilities],
  );

  return (
    <div className={style.profilePage}>
      <Header title={t('My Profile')} showLogo={true} />
      <div className={style.profileFieldsBlock}>
        <MainInfo isLoading={isLoading} control={control} identityProvider={identityProvider} />
        <SecurityInfo isLoading={isLoading} enabled2fa={fullProfileData?.user.use_2fa} />
        <ThemeBlock />
        <PersonalInfo
          isLoading={isLoading || isTimezoneLoading}
          control={control}
          languageList={languageListData}
          timezoneList={timezoneListData}
        />
        <DataUnitsInfo isLoading={isLoading} control={control} reloadTimeoutList={reloadTimeoutList} />
        <div className={style.notificationsInfoContainer}>
          <div className={style.containerTitle}>{t('Notifications settings')}</div>
          <div className={style.fieldsContainer}>
            <EmailNotificationsProfile
              locations={processedFacilityStructureData}
              activeLocationsIds={activeLocationsIds}
              isLoading={isLoading}
            />
          </div>
        </div>
        <AdditionalInfo
          isLoading={isLoading}
          control={control}
          allFacilities={fullProfileData?.user.all_facilities}
        />
      </div>
      {(isDirty || hasSubscriptionsChanges) && (
        <div className={style.btnsBlock}>
          <Button type="secondary" text={t('Reset All Changes')} onClick={handleResetChanges} />
          <Button
            type="primary"
            variant={'medium'}
            text={t('Save')}
            disabled={updateMutationLoading}
            onClick={saveData}
          />
        </div>
      )}
    </div>
  );
};

export default Profile;
